import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { bool, node, object } from 'prop-types';

import useJobMapJobBrands from './useJobMapBrands';
import useJobMapJobCategories from './useJobMapJobCategories';

export const JobMapContext = React.createContext({});

export default function JobMapProvider({ children, hasRemoteJobs, jobMap }) {
  const {
    id: jobMapId,
    brand: effectiveBrand,
    media_mode: mediaMode,
    map_pin_mode: mapPinMode,
    map_pin_thumb_url: mapPinUrl,
    name: jobMapName,
    logo_thumb_url: logo,
    primary_color: primaryColor,
    map_center_lat: mapCenterLat,
    map_center_lng: mapCenterLng,
    initial_zoom: initialZoom,
  } = jobMap;

  const isMapCenterConfigured = mapCenterLat !== null && mapCenterLng !== null;

  const defaultParams = {
    center: {
      lat: parseFloat(mapCenterLat) || 0,
      lng: parseFloat(mapCenterLng) || 0,
    },
    zoom: isMapCenterConfigured ? initialZoom : 1,
    q: '',
    category_names: [],
    page: 1,
    per_page: 25,
  };

  const [params, setParams] = useState(defaultParams);
  const [currentPlace, setCurrentPlace] = useState(null);
  const [currentJob, setCurrentJob] = useState(null);
  const [zoomOutToJobs, setZoomOutToJobs] = useState(null);
  const [showSidebar, setShowSidebar] = useState(
    !document.body.classList.contains('mobile')
  );
  const brands = useJobMapJobBrands(jobMapId);
  const mapRef = useRef();
  const allJobsBounds = useRef();

  const getBrand = useCallback(
    (brandId) => {
      let brand;
      if (brands && brands.data) {
        brand = brands.data.entries.find((x) => x.id === brandId);
      }
      return brand;
    },
    [brands.data]
  );

  const zoomToAllJobs = useCallback(() => {
    if (allJobsBounds.current && mapRef.current) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend({
        lat: allJobsBounds.current.north,
        lng: allJobsBounds.current.east,
      });
      bounds.extend({
        lat: allJobsBounds.current.south,
        lng: allJobsBounds.current.west,
      });
      mapRef.current.fitBounds(bounds);
    } else {
      setParams(defaultParams);
    }
  }, []);

  const zoomToUser = useCallback((lat, lng) => {
    if (mapRef.current) {
      const userLatLng = new window.google.maps.LatLng(lat, lng);
      const circle = new window.google.maps.Circle({
        radius: 25000,
        center: userLatLng,
      });
      mapRef.current.fitBounds(circle.getBounds());
    }
  }, []);

  let currentView = 'list';
  if (currentPlace) currentView = 'location';
  if (currentJob) currentView = 'detail';

  useEffect(() => {
    if (currentPlace || currentJob) {
      setShowSidebar(true);
    }
  }, [currentJob, currentPlace]);

  const {
    data: { entries: categoryNameOptions },
  } = useJobMapJobCategories(jobMapId);

  const contextValue = useMemo(
    () => ({
      allJobsBounds,
      categoryNameOptions,
      currentJob,
      currentPlace,
      currentView,
      effectiveBrand,
      getBrand,
      hasRemoteJobs,
      isMapCenterConfigured,
      jobMapId,
      jobMapName,
      logo,
      mapPinMode,
      mapPinUrl,
      mapRef,
      mediaMode,
      params,
      primaryColor,
      setCurrentJob,
      setCurrentPlace,
      setParams,
      setShowSidebar,
      setZoomOutToJobs,
      showSidebar,
      zoomOutToJobs,
      zoomToAllJobs,
      zoomToUser,
    }),
    [
      allJobsBounds,
      categoryNameOptions,
      currentJob,
      currentPlace,
      currentView,
      getBrand,
      hasRemoteJobs,
      isMapCenterConfigured,
      jobMapId,
      logo,
      mapPinMode,
      mapPinUrl,
      mapRef,
      mediaMode,
      params,
      primaryColor,
      setCurrentJob,
      setCurrentPlace,
      setParams,
      setShowSidebar,
      setZoomOutToJobs,
      showSidebar,
      zoomOutToJobs,
      zoomToAllJobs,
      zoomToUser,
    ]
  );

  return (
    <JobMapContext.Provider value={contextValue}>
      {children}
    </JobMapContext.Provider>
  );
}

JobMapProvider.propTypes = {
  children: node.isRequired,
  hasRemoteJobs: bool.isRequired,
  jobMap: object.isRequired,
};
